<script lang="ts">
    /* for every keydown event in input the function inputItem() is called, adds content to input array if last key was "Enter" and input is non-empty, reactively updates everything */
    /* clicking filter changes URL fragment, "hashchange" event handler calls applyFilter(), sets selectedFilter, reactively updates filteredItems */

    import { uuid } from "./helpers";
    import { slide } from "svelte/transition"

    type Item = {
        id: string;
        description: string;
        completed: boolean;
    };

    enum Filter {
        All = "",
        Active = "active",
        Completed = "completed"
    }

    let items: Item[];
    let selectedFilter: Filter = Filter.All;
    // let selectedItemToEditIndex: number = undefined;

    // load items from previous session if available
    try {
		items = JSON.parse(localStorage.getItem('todos-svelte')) || [];
	} catch (err) {
		items = [];
    }

    $: filteredItems =
        selectedFilter === Filter.Active
            ? items.filter(item => !item.completed)
            : selectedFilter === Filter.Completed
            ? items.filter(item => item.completed)
            : items;

    $: numCompleted = items.filter(item => item.completed).length;

    $: numActive = items.length - numCompleted;

    $: try {
		localStorage.setItem('todos-svelte', JSON.stringify(items));
	} catch (err) {
		// noop
	}

    function applyFilter() {
        const urlFragment = window.location.hash;

        if (urlFragment === `#/${Filter.Active}`) {
            selectedFilter = Filter.Active;
        } else if (urlFragment === `#/${Filter.Completed}`) {
            selectedFilter = Filter.Completed;
        } else {
            // is applied when URL fragment is `#/${Filter.All}`, or anything else including empty!
            selectedFilter = Filter.All;
        }
    }

    window.addEventListener("hashchange", applyFilter);
    // execute at least once in case opens page directly with fragment URL
    applyFilter();

    function inputItem(event: KeyboardEvent) {
        const key = event.key;
        const val = (event.target as HTMLInputElement).value.trim();

        if (key == "Enter" && val !== "") {
            addItem({
                id: uuid(),
                description: val,
                completed: false
            });
            (event.target as HTMLInputElement).value = "";
        }
    }

    function removeCompletedItems() {
        items = items.filter(item => !item.completed);
    }

    function removeItem(index: number) {
        items = [...items.slice(0, index), ...items.slice(index + 1)];
    }

    function addItem(item: Item) {
        items = [...items, item];
    }

</script>

<!-- TODO: toggle-all button, edit functionality -->

<h1>todos</h1>

<section class="todo-app">
    <section class="todo-header">
        <div class="empty"></div>
        <input
        class="todo-input"
        placeholder="What needs to be done?"
        on:keydown={inputItem}
        autofocus
        size="22"/>
    </section>

    {#if items.length > 0}
        <section class="todo-body">

            <ul class="todo-list">
                {#each filteredItems as item, index (item.id)}
                    <li transition:slide class="todo-item {item.completed ? 'completed' : ''}">
                        <input
                            class="completeToggle"
                            type="checkbox"
                            bind:checked={item.completed} />
                        <p class="description">{item.description}</p>
                        <button
                            class="removeItem"
                            on:click={() => removeItem(index)}>&times;</button>
                    </li>
                {/each}
            </ul>

            <section class="todo-footer">
                <button
                class="removeCompletedItems {numCompleted ? '' : 'visibilityCollapse'}"
                on:click={removeCompletedItems}>Clear completed</button>

                <ul class="filter">
                    <li>
                        <a
                            href={`#/${Filter.All}`}
                            class={selectedFilter == Filter.All ? 'checked' : ''}>All</a>
                    </li>
                    <li>
                        <a
                            href={`#/${Filter.Active}`}
                            class={selectedFilter == Filter.Active ? 'checked' : ''}>Active</a>
                    </li>
                    <li>
                        <a
                            href={`#/${Filter.Completed}`}
                            class={selectedFilter == Filter.Completed ? 'checked' : ''}>Completed</a>
                    </li>
                </ul>

                <p class="todo-count">{numActive} {numActive === 1 ? 'item' : 'items'} remaining</p>
            </section>
        </section>
    {/if}
</section>

<style>
    /* TODO: sort layout vs style */

    h1 {
        align-self: center;
    }

    h1 {
        color: var(--red-200);
        font-size: 7rem;
        font-weight: 100;
    }

    .todo-app {
        flex-direction: column;
        background: white;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 25px 50px 0 rgba(0, 0, 0, 0.1);
        /* containing block for absolutely positioned todo-footer::after */
        position: relative;
    }

    /* todo header */

    .todo-header {
        box-shadow: inset 0 -2px 1px rgba(0, 0, 0, 0.03);
    }

    .empty {
        flex: 0 1 4.5rem;
    }

    .todo-input {
        flex: 1;
        padding: 1rem 0;
        font-size: 1.5rem;
    }

    .todo-input::placeholder {
        font-style: italic;
        font-weight: 300;
        color: var(--grey-400);
    }

    /* todo body */

    .todo-body {
        flex-direction: column;
        border-top: 1px solid var(--grey-300);

        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2), 0 8px 0 -3px #f6f6f6,
            0 9px 1px -3px rgba(0, 0, 0, 0.2), 0 16px 0 -6px #f6f6f6,
            0 17px 2px -6px rgba(0, 0, 0, 0.2);
    }

    .todo-list {
        flex-direction: column;
    }

    .todo-item {
        flex-direction: row;
        align-items: center;
        column-gap: 1rem;

        font-size: 1.5rem;
        border-bottom: 1px solid var(--grey-300);
    }

    .completeToggle {
        margin-left: 0.5rem;
    }

    .description {
        flex: 1;
        transition: color 0.4s;
    }

    .todo-item.completed .description {
        color: var(--grey-400);
        text-decoration: line-through;
    }

    .todo-list .removeItem {
        visibility: collapse;
        font-size: 3rem;
        /* margin-bottom hack to center icon vertically, needs leading-trim property */
        margin: auto 0.5rem 0.6rem 0;
        color: var(--red-400);
        transition: color 0.2s ease-out;
        cursor: pointer;
    }

    .todo-list .removeItem:hover {
        color: var(--red-500);
    }

    .todo-list li:hover .removeItem {
        visibility: visible;
    }

    /* todo footer */

    .todo-footer {
        color: var(--grey-600);
        padding: 0.5rem;
        align-items: center;
    }

    .filter {
        flex: 1;
        justify-content: center;
        column-gap: 0.5rem;
        flex-wrap: wrap;
    }

    .filter li a {
        color: inherit;
        padding: 0.2rem;
        border: 1px solid transparent;
        border-radius: 3px;
        cursor: pointer;
    }

    .filter li a:hover {
        border-color: var(--red-200);
    }

    .filter li a.checked {
        border-color: var(--red-300);
    }

    .removeCompletedItems {
        margin-left: auto;
        cursor: pointer;
        color: var(--red-400);
    }

    .visibilityCollapse {
        visibility: collapse;
    }

    .removeCompletedItems:hover {
        color: var(--red-500);
    }
</style>
